'use client'

import cn from 'classnames';
import { useTranslations } from 'next-intl';
import { ReactNode } from 'react';

import { Link } from '@lib/navigation';
import TickSvg from 'public/static/svg/tick.svg';
import { ArticleViewType } from 'types/article';

export interface PremiumBannerLabels {
  title: ReactNode;
  titlePodcast: ReactNode;
  titleVideo: ReactNode;
  subtitle: string;
  list: {
    noAds: ReactNode;
    allArticles: ReactNode;
    allMagazines: ReactNode;
    allWebinars: ReactNode;
  };
  link: string;
  button: string;
  note: string;
  footer: {
    text: string;
    link: string;
  };
}

interface ArticlePremiumBannerProps {
  className?: string;
  currentPath?: string;
  viewType: ArticleViewType;
  triggerAnalyticsEvents?: boolean;
  article?: any;
}
export default function ArticlePremiumBanner({
  className,
  currentPath,
  viewType,
}: ArticlePremiumBannerProps) {
  const tPremium = useTranslations('premium');
  const getLoginLink = () => {
    if (currentPath) {
      return `/login?from=${currentPath}`;
    }

    return '/login';
  };
  const articleLabelListItemProcess = {
    strong: (chunks) => <strong>{chunks}</strong>,
  };
  const labels = {
    title: tPremium.rich('forPremium.title', {
      premium: (chunks) => <span className="text-orange-500">{chunks}</span>,
    }),
    titlePodcast: tPremium.rich('forPremium.titlePodcast', {
      premium: (chunks) => <span className="text-orange-500">{chunks}</span>,
    }),
    titleVideo: tPremium.rich('forPremium.titleVideo', {
      premium: (chunks) => <span className="text-orange-500">{chunks}</span>,
    }),
    subtitle: tPremium('forPremium.subtitle'),

    list: {
      noAds: tPremium.rich(
        'forPremium.list.noAds',
        articleLabelListItemProcess,
      ),
      allArticles: tPremium.rich(
        'forPremium.list.allArticles',
        articleLabelListItemProcess,
      ),
      allMagazines: tPremium.rich(
        'forPremium.list.allMagazines',
        articleLabelListItemProcess,
      ),
      allWebinars: tPremium.rich(
        'forPremium.list.allWebinars',
        articleLabelListItemProcess,
      ),
    },
    link: tPremium('forPremium.link'),
    button: tPremium('forPremium.button'),
    note: tPremium('forPremium.note'),
    footer: {
      text: tPremium('forPremium.footer.text'),
      link: tPremium('forPremium.footer.link'),
    },
  }

  const listKeys = ['noAds', 'allArticles', 'allMagazines', 'allWebinars'];


  return (
    <div
      className={cn(
        'rounded-2xl bg-premium-dark px-8 py-6 text-xs font-light text-white lg:px-12 lg:py-8 lg:text-base',
        className,
      )}
    >
      <h3 className="mb-1 text-center text-lg font-bold lg:text-2xl">
        {viewType === 'article' && (
          <>{labels.title}</>
        )}
        {viewType === 'video' && (
          <>{labels.titleVideo}</>
        )}
        {viewType === 'podcast' && (
          <>{labels.titlePodcast}</>
        )}
      </h3>
      <p className="mb-6 text-center">{labels.subtitle}</p>

      <ul className="mx-auto mb-6 md:max-w-96">
        {listKeys.map((key) => (
          <li key={key} className="mb-3 flex items-center lg:px-2">
            <TickSvg className="mr-3 h-8 w-8 flex-shrink-0 text-premium-dark" />
            <span>{labels.list[key]}</span>
          </li>
        ))}
      </ul>

      <div className="mx-auto my-4 w-3/4 border-t-2 border-gray-500" />

      <p className="text-center font-medium lg:text-lg">
        {labels.footer.text}
        <Link href={getLoginLink()} rel="nofollow" className="ml-1 underline">
          {labels.footer.link}
        </Link>
      </p>
    </div>
  );
}
